/**
 * ImpersonateButton — per-row action in the Beheerconsole UsersTab.
 *
 * Visible only when the signed-in user has app_role === 'superadmin'
 * (defense-in-depth — requireAdmin on the API is the real gate).
 *
 * Flow: confirm dialog → POST /api/admin/impersonate/:userId →
 * clerk.client.signIn.create({ strategy: 'ticket', ticket: token }) →
 * clerk.setActive({ session: createdSessionId }) → redirect to /.
 *
 * Source: Phase 10 D-09.
 */
import React, { useState } from 'react';
import { useClerk } from '@clerk/react';
import { useAuth } from '../../../hooks/useAuth';
import { adminApi } from '../../../components/admin/api';

export interface ImpersonateButtonProps {
  targetUserId: number;
  targetEmail: string;
}

export default function ImpersonateButton({ targetUserId, targetEmail }: ImpersonateButtonProps): React.ReactElement | null {
  const { user } = useAuth();
  const clerk = useClerk();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Defense-in-depth only — requireAdmin is the real server-side gate.
  if (user?.role !== 'superadmin') return null;

  async function go() {
    if (!window.confirm(`Impersonate ${targetEmail}? Deze handeling wordt geaudit.`)) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await adminApi.impersonate(targetUserId);
      // Clerk ticket strategy — documented impersonation pattern.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (clerk as any).client.signIn.create({ strategy: 'ticket', ticket: token });
      await clerk.setActive({ session: result.createdSessionId });
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impersonate mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid={`impersonate-${targetUserId}`}>
      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 hover:underline disabled:opacity-50"
      >
        {busy ? 'Bezig…' : 'Impersonate'}
      </button>
      {error && (
        <span role="alert" className="block text-[10px] text-red-600 dark:text-red-400 mt-0.5">
          {error}
        </span>
      )}
    </div>
  );
}
